import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Button, Space, Spin, Alert, Tag, Image, theme } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconPhoto } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminSpaces, IAdminSpaceSummary } from '../../../services/AdminService';
import {
  getPhotoModerationQueue,
  getSpacePhotosForModeration,
  flagPhotoForModeration,
  approvePhotoModeration,
  rejectPhotoModeration,
  IAdminPhoto,
} from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminReasonModal from '../../../components/Admin/AdminReasonModal';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';

const { Title } = Typography;
const { Search } = Input;

// Queue of photos pending manual review -- 100% manual for now (no Azure AI
// Content Safety integration yet); approve/reject flow slots in here without
// changing the queue shape once that's wired up.
const PhotoModerationSection: React.FC = () => {
  const { token } = theme.useToken();
  const { t } = useTranslation();
  const [queue, setQueue] = useState<IAdminPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const [searchResults, setSearchResults] = useState<IAdminSpaceSummary[]>([]);
  const [searching, setSearching] = useState(false);

  const [selectedSpace, setSelectedSpace] = useState<IAdminSpaceSummary | null>(null);
  const [spacePhotos, setSpacePhotos] = useState<IAdminPhoto[]>([]);
  const [spacePhotosLoading, setSpacePhotosLoading] = useState(false);

  const [rejectTarget, setRejectTarget] = useState<string | null>(null);

  const loadQueue = useCallback(() => {
    setLoading(true);
    setError(false);
    getPhotoModerationQueue()
      .then(setQueue)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const handleSearch = (value: string) => {
    if (!value) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    getAdminSpaces(undefined, value)
      .then(setSearchResults)
      .catch(() => message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR)))
      .finally(() => setSearching(false));
  };

  const openSpacePhotos = (space: IAdminSpaceSummary) => {
    setSelectedSpace(space);
    setSpacePhotosLoading(true);
    getSpacePhotosForModeration(space.id)
      .then(setSpacePhotos)
      .catch(() => message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR)))
      .finally(() => setSpacePhotosLoading(false));
  };

  const refreshSpacePhotos = async () => {
    if (!selectedSpace) return;
    const updated = await getSpacePhotosForModeration(selectedSpace.id);
    setSpacePhotos(updated);
  };

  const handleFlag = async (photoId: string) => {
    try {
      await flagPhotoForModeration(photoId);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      await refreshSpacePhotos();
      loadQueue();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleApprove = async (photoId: string) => {
    try {
      await approvePhotoModeration(photoId);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      await refreshSpacePhotos();
      loadQueue();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const handleReject = async (reason: string) => {
    if (!rejectTarget) return;
    try {
      await rejectPhotoModeration(rejectTarget, reason);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      setRejectTarget(null);
      await refreshSpacePhotos();
      loadQueue();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    }
  };

  const renderPhotoCard = (photo: IAdminPhoto) => (
    <div
      key={photo.id}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        width: 200,
        padding: 8,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 8,
      }}
    >
      <Image src={photo.thumbnailUrl || photo.url} alt="" style={{ objectFit: 'cover', borderRadius: 4 }} height={140} width={184} />
      <Tag color={photo.moderationStatus === 'Pending' ? 'orange' : 'green'} style={{ width: 'fit-content' }}>
        {photo.moderationStatus === 'Pending'
          ? translate(t, TRANSLATIONS.ADMIN_PHOTOS_STATUS_PENDING)
          : translate(t, TRANSLATIONS.ADMIN_PHOTOS_STATUS_APPROVED)}
      </Tag>
      <Space wrap>
        {photo.moderationStatus === 'Pending' ? (
          <Button size="small" type="primary" onClick={() => handleApprove(photo.id)}>
            {translate(t, TRANSLATIONS.ADMIN_PHOTOS_APPROVE_BUTTON)}
          </Button>
        ) : (
          <Button size="small" onClick={() => handleFlag(photo.id)}>
            {translate(t, TRANSLATIONS.ADMIN_PHOTOS_FLAG_BUTTON)}
          </Button>
        )}
        <Button size="small" danger onClick={() => setRejectTarget(photo.id)}>
          {translate(t, TRANSLATIONS.ADMIN_PHOTOS_REJECT_BUTTON)}
        </Button>
      </Space>
    </div>
  );

  return (
    <div>
      <Title level={4} style={{ marginBottom: 20 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_PHOTO_MODERATION)}
      </Title>

      <Search
        allowClear
        loading={searching}
        placeholder={translate(t, TRANSLATIONS.ADMIN_PHOTOS_SEARCH_PLACEHOLDER)}
        style={{ maxWidth: 360, width: '100%', marginBottom: 16 }}
        onSearch={handleSearch}
      />

      {searchResults.length > 0 && (
        <AdminResponsiveList<IAdminSpaceSummary>
          rowKey="id"
          dataSource={searchResults}
          pagination={false}
          size="small"
          style={{ marginBottom: 24 }}
          toCard={(record) => ({
            key: record.id,
            title: record.title,
            fields: [{ label: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_LOCATION), value: `${record.city}, ${record.country}` }],
            actions: (
              <Button size="small" onClick={() => openSpacePhotos(record)}>
                {translate(t, TRANSLATIONS.ADMIN_PHOTOS_VIEW_PHOTOS_BUTTON)}
              </Button>
            ),
          })}
          columns={[
            { title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_TITLE), dataIndex: 'title', key: 'title' },
            {
              title: translate(t, TRANSLATIONS.ADMIN_SPACES_COL_LOCATION),
              key: 'location',
              render: (_: unknown, record: IAdminSpaceSummary) => `${record.city}, ${record.country}`,
            },
            {
              title: '',
              key: 'actions',
              render: (_: unknown, record: IAdminSpaceSummary) => (
                <Button size="small" onClick={() => openSpacePhotos(record)}>
                  {translate(t, TRANSLATIONS.ADMIN_PHOTOS_VIEW_PHOTOS_BUTTON)}
                </Button>
              ),
            },
          ]}
        />
      )}

      {selectedSpace && (
        <>
          <Title level={5} style={{ marginBottom: 12 }}>
            {translate(t, TRANSLATIONS.ADMIN_PHOTOS_SPACE_PHOTOS_TITLE)} — {selectedSpace.title}
          </Title>
          {spacePhotosLoading ? (
            <div style={{ textAlign: 'center', padding: 32 }}>
              <Spin />
            </div>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
              {spacePhotos.map(renderPhotoCard)}
            </div>
          )}
        </>
      )}

      <Title level={5} style={{ marginBottom: 12 }}>
        {translate(t, TRANSLATIONS.ADMIN_PHOTOS_QUEUE_TITLE)}
      </Title>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_PHOTOS_QUEUE_LOAD_ERROR)} />
      ) : queue.length === 0 ? (
        <EmptyState
          icon={<IconPhoto size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_PHOTOS_QUEUE_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_PHOTOS_QUEUE_EMPTY_DESCRIPTION)}
        />
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>{queue.map(renderPhotoCard)}</div>
      )}

      <AdminReasonModal
        open={rejectTarget !== null}
        title={translate(t, TRANSLATIONS.ADMIN_PHOTOS_REJECT_MODAL_TITLE)}
        confirmLabel={translate(t, TRANSLATIONS.ADMIN_PHOTOS_REJECT_BUTTON)}
        onCancel={() => setRejectTarget(null)}
        onConfirm={handleReject}
      />
    </div>
  );
};

export default PhotoModerationSection;
